/**
 * MoonPay client-credential crypto (no SDK).
 *
 * The frames return an ENCRYPTED `credentials` blob sealed to a client-generated
 * X25519 public key. We decrypt it with X25519 ECDH → HKDF-SHA256 → AES-GCM to
 * recover { accessToken, clientToken }. Uses generic @noble primitives (also a
 * viem dependency) — NOT the MoonPay SDK.
 *
 * Verified against MoonPay's documented scheme (dev.moonpay.com/platform/guides/
 * manual-integration/web).
 */

import { gcm } from '@noble/ciphers/aes';
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';

export interface MoonpayKeyPair {
  privateKeyHex: string;
  publicKeyHex: string;
}

export interface ClientCredentials {
  accessToken: string;
  clientToken: string;
  expiresAt?: string;
}

/** Generate an ephemeral X25519 keypair (hex-encoded) for one frame exchange. */
export function generateKeyPair(): MoonpayKeyPair {
  const privateKey = x25519.utils.randomPrivateKey();
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKeyHex: bytesToHex(privateKey), publicKeyHex: bytesToHex(publicKey) };
}

/**
 * Decrypt the `credentials` string returned by a MoonPay frame.
 * Blob = base64(JSON{ ephemeralPublicKey, iv, ciphertext } as hex).
 */
export function decryptClientCredentials(
  encryptedBase64: string,
  privateKeyHex: string,
): ClientCredentials {
  const json = JSON.parse(atob(encryptedBase64)) as {
    ephemeralPublicKey: string;
    iv: string;
    ciphertext: string;
  };

  const sharedSecret = x25519.getSharedSecret(
    hexToBytes(privateKeyHex),
    hexToBytes(json.ephemeralPublicKey),
  );
  const derivedKey = hkdf(sha256, sharedSecret, undefined, undefined, 32);
  const decrypted = gcm(derivedKey, hexToBytes(json.iv)).decrypt(hexToBytes(json.ciphertext));
  return JSON.parse(new TextDecoder().decode(decrypted)) as ClientCredentials;
}
